import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { adminApi, isUnauthorized } from '../services/adminApi';
import WatchlistAddEditModal from '../components/WatchlistAddEditModal';
import UndoSnackbar from '../components/UndoSnackbar';
import { ensureSymbolColorsAssigned, getSymbolColor } from '../utils/symbolColors';

interface WatchlistScreenProps {
  isDarkMode: boolean;
  onOpenResearch?: (symbol: string) => void;
  authToken?: string | null;
  onUnauthorized?: () => void;
}

type WatchlistRow = {
  id?: number;
  symbol: string;
  currentPrice: number;
  changePct: number;
  buyPriceAlert: number | null;
  sellPriceAlert: number | null;
  status: string;
};

const WATCHLIST_DELETE_UNDO_MS = 6500;
const WATCHLIST_ADD_UNDO_MS = 6500;
const WATCHLIST_EDIT_UNDO_MS = 6500;
const WATCHLIST_SKIP_DELETE_CONFIRM_KEY = 'watchlist_skip_delete_confirm';

const WatchlistScreen: React.FC<WatchlistScreenProps> = ({
  isDarkMode,
  onOpenResearch,
  authToken,
  onUnauthorized,
}) => {
  const colors = useMemo(() => {
    const dark = isDarkMode;
    return {
      pageBg: dark ? '#0b1220' : '#ffffff',
      text: dark ? '#e5e7eb' : '#0f172a',
      subText: dark ? '#94a3b8' : '#64748b',
      cardBg: dark ? '#0f172a' : '#ffffff',
      border: dark ? '#374151' : '#e2e8f0',
      headerBg: dark ? '#1F2937' : '#f8fafc',
      hoverBg: dark ? '#0b1730' : '#f1f5f9',
      buttonBg: dark ? '#111827' : '#ffffff',
      buttonBorder: dark ? '#3B82F6' : '#93c5fd',
      buttonText: dark ? '#3B82F6' : '#2563eb',
      menuBg: dark ? '#0b1220' : '#ffffff',
      menuShadow: dark ? '0 18px 46px rgba(0,0,0,0.65)' : '0 18px 46px rgba(15,23,42,0.18)',
      buy: '#ef4444',
      sell: '#f97316',
      neutral: dark ? '#94a3b8' : '#9ca3af',
      success: '#16a34a',
    };
  }, [isDarkMode]);

  const [watchlistRows, setWatchlistRows] = useState<WatchlistRow[]>([]);
  const [symbolColorVersion, setSymbolColorVersion] = useState(0);
  const [tableScrollbarWidth, setTableScrollbarWidth] = useState(0);
  const [hasTableScrollbar, setHasTableScrollbar] = useState(false);
  const watchlistRowsRef = useRef<WatchlistRow[]>([]);
  const lastQuotesUpdatedRef = useRef<Map<string, string>>(new Map());
  const tableWrapRef = useRef<HTMLDivElement | null>(null);
  const [isQuotesRefreshing, setIsQuotesRefreshing] = useState(false);
  const refreshTintTimeoutRef = useRef<number | null>(null);

  type PendingUndo = { message: string; timerId: number };

  type PendingWatchlistAddUndo = { symbol: string; timerId: number };
  const [pendingWatchlistAddUndo, setPendingWatchlistAddUndo] = useState<PendingUndo | null>(null);
  const pendingWatchlistAddUndoRef = useRef<PendingWatchlistAddUndo | null>(null);

  type PendingWatchlistEditUndo = { snapshot: WatchlistRow; timerId: number };
  const [pendingWatchlistEditUndo, setPendingWatchlistEditUndo] = useState<PendingUndo | null>(null);
  const pendingWatchlistEditUndoRef = useRef<PendingWatchlistEditUndo | null>(null);

  const lastEditSnapshotRef = useRef<WatchlistRow | null>(null);

  type PendingWatchlistDelete = {
    symbol: string;
    row: WatchlistRow;
    index: number;
    timerId: number;
  };
  const [pendingWatchlistDelete, setPendingWatchlistDelete] = useState<PendingWatchlistDelete | null>(null);
  const pendingWatchlistDeleteRef = useRef<PendingWatchlistDelete | null>(null);

  const [watchlistError, setWatchlistError] = useState<string | null>(null);

  useEffect(() => {
    const probe = document.createElement('div');
    probe.style.width = '100px';
    probe.style.height = '100px';
    probe.style.overflow = 'scroll';
    probe.style.position = 'absolute';
    probe.style.top = '-9999px';
    document.body.appendChild(probe);
    const width = probe.offsetWidth - probe.clientWidth;
    document.body.removeChild(probe);
    setTableScrollbarWidth(Number.isFinite(width) ? width : 0);
  }, []);

  useEffect(() => {
    const update = () => {
      const el = tableWrapRef.current;
      if (!el) return;
      setHasTableScrollbar(el.scrollHeight > el.clientHeight + 1);
    };

    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [watchlistRows.length]);

  const normalizeSymbol = useCallback((raw: string): string => String(raw || '').trim().toUpperCase(), []);

  const handleTableKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    const el = tableWrapRef.current;
    if (!el) return;

    const target = event.target as HTMLElement | null;
    if (target) {
      const tag = target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) {
        return;
      }
    }

    const lineStep = 40;
    const pageStep = Math.max(60, el.clientHeight - 40);

    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        el.scrollBy({ top: -lineStep, behavior: 'auto' });
        break;
      case 'ArrowDown':
        event.preventDefault();
        el.scrollBy({ top: lineStep, behavior: 'auto' });
        break;
      case 'PageUp':
        event.preventDefault();
        el.scrollBy({ top: -pageStep, behavior: 'auto' });
        break;
      case 'PageDown':
        event.preventDefault();
        el.scrollBy({ top: pageStep, behavior: 'auto' });
        break;
      case 'Home':
        event.preventDefault();
        el.scrollTo({ top: 0, behavior: 'auto' });
        break;
      case 'End':
        event.preventDefault();
        el.scrollTo({ top: el.scrollHeight, behavior: 'auto' });
        break;
      default:
        break;
    }
  }, []);

  const computeAlertStatus = useCallback((
    currentPrice: number,
    buyPriceAlert: number | null,
    sellPriceAlert: number | null
  ): WatchlistRow['status'] => {
    const price = Number(currentPrice);
    const buy = buyPriceAlert == null ? NaN : Number(buyPriceAlert);
    const sell = sellPriceAlert == null ? NaN : Number(sellPriceAlert);

    const hasBuy = Number.isFinite(buy) && buy !== 0;
    const hasSell = Number.isFinite(sell) && sell !== 0;
    if (!hasBuy && !hasSell) return '';

    if (!Number.isFinite(price)) return 'Not Reached';

    if (hasSell && sell <= price) return 'Sell Triggered';
    if (hasBuy && price <= buy) return 'Buy Triggered';
    return 'Not Reached';
  }, []);

  const renderAlertStatus = (row: WatchlistRow) => {
    const text = String(row.status || '').trim();
    if (!text) return null;

    const isTriggered = text === 'Buy Triggered' || text === 'Sell Triggered';
    const target = text === 'Sell Triggered' || (row.sellPriceAlert != null && row.buyPriceAlert == null)
      ? row.sellPriceAlert
      : row.buyPriceAlert;
    const targetLabel = text === 'Sell Triggered' || (row.sellPriceAlert != null && row.buyPriceAlert == null)
      ? 'Sell Target'
      : 'Buy Target';
    const distancePct = target != null && Number.isFinite(Number(target)) && Number(target) !== 0 && Number.isFinite(row.currentPrice)
      ? Math.abs((row.currentPrice - Number(target)) / Number(target)) * 100
      : null;

    if (!isTriggered && distancePct != null && distancePct <= 10) {
      const isClose = distancePct <= 5;
      const thresholdLabel = isClose ? 'Within 5%' : 'Within 10%';
      return (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 185,
            fontSize: 14,
            height: 31,
            boxSizing: 'border-box',
            borderRadius: 20,
            gap: 10,
            padding: '6px 10px',
            background: isClose ? '#FF572224' : '#FFC10724',
            color: isClose ? '#c2410c' : '#8a5a00',
            whiteSpace: 'nowrap',
            lineHeight: 1.2,
          }}
        >
          {`${thresholdLabel} of ${targetLabel}`}
        </span>
      );
    }

    const iconSrc = text === 'Not Reached' ? '/images/grayalert.svg' : '/images/redalert.svg';
    const isNotReached = text === 'Not Reached';

    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          lineHeight: 1,
          marginLeft: isNotReached ? -10 : 0,
        }}
      >
        <span
          style={{
            width: 18,
            height: 18,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: '0 0 auto',
          }}
        >
          {isNotReached ? (
            <img src={iconSrc} alt="" aria-hidden="true" style={{ width: 18, height: 18, display: 'block' }} />
          ) : (
            <>
              <img src="/images/alert-flicker.gif" alt="" aria-hidden="true" style={{ width: 18, height: 18, display: 'block' }} />
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true" style={{ display: 'none' }}>
              <path
                d="M4.93805 17.1447C5.67087 17.7586 6.53875 18.1901 7.47055 18.4038C7.55971 18.433 7.61971 18.3163 7.55971 18.258C5.56388 16.3538 6.66055 14.293 7.47055 13.3638C8.04805 12.7005 8.87055 11.608 8.81055 10.143C8.81055 9.99634 8.96055 9.87884 9.07888 9.96717C10.2114 10.553 10.9855 11.8422 11.1939 12.8963C11.5222 12.5747 11.6414 12.0763 11.6414 11.6372C11.6414 11.4905 11.8197 11.373 11.9689 11.4905C13.0414 12.4572 14.888 15.738 11.9089 18.3155C11.8497 18.3747 11.9089 18.4922 11.9689 18.463C12.8722 18.2262 13.7217 17.8183 14.4714 17.2613C19.3272 13.5113 16.1689 6.86134 14.113 4.66467C13.8455 4.40051 13.3689 4.57634 13.3689 4.95717C13.3389 5.74884 13.1005 6.62717 12.4755 7.21301C11.9989 5.22134 10.4105 2.95884 8.14638 1.84551C7.84888 1.69884 7.49138 1.93301 7.52138 2.25551C7.57971 4.98051 5.80221 6.71551 4.25305 8.97134C2.88221 10.9922 1.98888 14.713 4.93805 17.1447Z"
                fill="#FF5722"
              />
              <path
                className="alert-flame-flicker"
                style={{
                  animation: 'alertFlameFlicker 1.8s ease-in-out infinite',
                  filter: 'drop-shadow(0 0 2px rgba(255, 87, 34, 0.65))',
                }}
                d="M4.93805 17.1447C5.67087 17.7586 6.53875 18.1901 7.47055 18.4038C7.55971 18.433 7.61971 18.3163 7.55971 18.258C5.56388 16.3538 6.66055 14.293 7.47055 13.3638C8.04805 12.7005 8.87055 11.608 8.81055 10.143C8.81055 9.99634 8.96055 9.87884 9.07888 9.96717C10.2114 10.553 10.9855 11.8422 11.1939 12.8963C11.5222 12.5747 11.6414 12.0763 11.6414 11.6372C11.6414 11.4905 11.8197 11.373 11.9689 11.4905C13.0414 12.4572 14.888 15.738 11.9089 18.3155C11.8497 18.3747 11.9089 18.4922 11.9689 18.463C12.8722 18.2262 13.7217 17.8183 14.4714 17.2613C19.3272 13.5113 16.1689 6.86134 14.113 4.66467C13.8455 4.40051 13.3689 4.57634 13.3689 4.95717C13.3389 5.74884 13.1005 6.62717 12.4755 7.21301C11.9989 5.22134 10.4105 2.95884 8.14638 1.84551C7.84888 1.69884 7.49138 1.93301 7.52138 2.25551C7.57971 4.98051 5.80221 6.71551 4.25305 8.97134C2.88221 10.9922 1.98888 14.713 4.93805 17.1447Z"
                fill="#FF7A50"
              />
            </svg>
            </>
          )}
        </span>
        <span
          style={
            isNotReached
              ? { display: 'inline-flex', alignItems: 'center', minHeight: 18, fontWeight: 400, color: '#5A5A5A', lineHeight: 1, transform: 'translateY(1px)' }
              : { display: 'inline-flex', alignItems: 'center', minHeight: 18, lineHeight: 1, transform: 'translateY(1px)' }
          }
        >
          {text}
        </span>
      </span>
    );
  };

  const getAlertStatusSortMeta = useCallback((row: WatchlistRow): { rank: number; text: string } => {
    const text = String(row.status || '').trim();
    if (!text) return { rank: 4, text: '' };

    const isTriggered = text === 'Buy Triggered' || text === 'Sell Triggered';
    if (isTriggered) return { rank: 0, text };

    const target = text === 'Sell Triggered' || (row.sellPriceAlert != null && row.buyPriceAlert == null)
      ? row.sellPriceAlert
      : row.buyPriceAlert;
    const targetLabel = text === 'Sell Triggered' || (row.sellPriceAlert != null && row.buyPriceAlert == null)
      ? 'Sell Target'
      : 'Buy Target';
    const distancePct = target != null && Number.isFinite(Number(target)) && Number(target) !== 0 && Number.isFinite(row.currentPrice)
      ? Math.abs((row.currentPrice - Number(target)) / Number(target)) * 100
      : null;

    if (distancePct != null && distancePct <= 10) {
      const isClose = distancePct <= 5;
      return {
        rank: isClose ? 1 : 2,
        text: `Within ${isClose ? '5%' : '10%'} of ${targetLabel}`,
      };
    }

    return { rank: 3, text };
  }, []);

  const loadWatchlist = useCallback(async () => {
    setWatchlistError(null);
    try {
      if (!authToken) {
        setWatchlistRows([]);
        setWatchlistError('You must be logged in to view My Watchlist.');
        return;
      }

      const items = await adminApi.listMyWatchlist(authToken);
      const rows: WatchlistRow[] = (items ?? []).map((it) => {
        const currentPrice = Number(it.currentPrice ?? 0) || 0;
        const buy = it.buyPriceAlert == null ? null : (Number(it.buyPriceAlert) || 0);
        const sell = it.sellPriceAlert == null ? null : (Number(it.sellPriceAlert) || 0);
        return {
          id: it.id,
          symbol: normalizeSymbol(it.symbol),
          currentPrice,
          changePct: Number(it.changePct ?? 0) || 0,
          buyPriceAlert: buy,
          sellPriceAlert: sell,
          status: computeAlertStatus(currentPrice, buy, sell),
        };
      });

      // If a delete is pending (undo available), keep it hidden from the list.
      const pendingSym = pendingWatchlistDeleteRef.current?.symbol;
      const filtered = pendingSym ? rows.filter((r) => normalizeSymbol(r.symbol) !== pendingSym) : rows;

      setWatchlistRows(filtered);
    } catch (err) {
      if (isUnauthorized(err)) {
        onUnauthorized?.();
        return;
      }
      setWatchlistError(err instanceof Error ? err.message : 'Failed loading watchlist');
    }
  }, [authToken, computeAlertStatus, normalizeSymbol, onUnauthorized]);

  // On entry: load watchlist from DB for this user (if logged in).
  // Also reload when authToken changes (e.g., new user login)
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (cancelled) return;
      // Clear existing data before loading new user's watchlist
      setWatchlistRows([]);
      setWatchlistError(null);
      await loadWatchlist();
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [loadWatchlist, authToken]);

  useEffect(() => {
    if (!authToken) return;
    const handler = () => {
      void loadWatchlist();
    };
    window.addEventListener('watchlist-updated', handler);
    return () => window.removeEventListener('watchlist-updated', handler);
  }, [authToken, loadWatchlist]);

  useEffect(() => {
    watchlistRowsRef.current = watchlistRows;
  }, [watchlistRows]);

  useEffect(() => {
    if (!authToken) return;

    let disposed = false;
    let timerId: number | undefined;
    const scheduleNext = (delayMs: number) => {
      if (timerId != null) window.clearTimeout(timerId);
      timerId = window.setTimeout(() => {
        if (!disposed) void refreshQuotes();
      }, delayMs);
    };

    const refreshQuotes = async () => {
      const rows = watchlistRowsRef.current;
      if (!rows.length) {
        setIsQuotesRefreshing(false);
        if (!disposed) {
          scheduleNext(15000);
        }
        return;
      }

      const symbols = Array.from(
        new Set(rows.map((r) => normalizeSymbol(r.symbol)).filter(Boolean))
      );
      if (!symbols.length) {
        setIsQuotesRefreshing(false);
        if (!disposed) {
          scheduleNext(15000);
        }
        return;
      }

      try {
        if (refreshTintTimeoutRef.current != null) {
          window.clearTimeout(refreshTintTimeoutRef.current);
          refreshTintTimeoutRef.current = null;
        }
        setIsQuotesRefreshing(true);
        refreshTintTimeoutRef.current = window.setTimeout(() => {
          setIsQuotesRefreshing(false);
          refreshTintTimeoutRef.current = null;
        }, 2000);
        const result = await adminApi.getQuotesCacheWithMeta(authToken, symbols);
        const quotes = result?.rows ?? [];
        const ttlSecRaw = result?.meta?.ttlSec;
        const ttlSec = Number.isFinite(ttlSecRaw) ? Number(ttlSecRaw) : null;
        if (disposed) return;

        const lastUpdatedBySymbol = lastQuotesUpdatedRef.current;
        const bySymbol = new Map(
          (quotes ?? []).map((q) => [normalizeSymbol(q.symbol), q])
        );

        setWatchlistRows((prev) => {
          let changed = false;
          const next = prev.map((row) => {
            const cached = bySymbol.get(normalizeSymbol(row.symbol));
            if (!cached || cached.currentPrice == null) return row;

            const nextPrice = Number(cached.currentPrice);
            const nextChange = cached.changePct == null ? row.changePct : Number(cached.changePct);

            if (Number.isNaN(nextPrice)) return row;

            const updatedStatus = computeAlertStatus(nextPrice, row.buyPriceAlert, row.sellPriceAlert);
            const isSamePrice = row.currentPrice === nextPrice;
            const isSameChange = row.changePct === nextChange;
            const isSameStatus = row.status === updatedStatus;

            if (isSamePrice && isSameChange && isSameStatus) return row;

            changed = true;
            return {
              ...row,
              currentPrice: nextPrice,
              changePct: nextChange,
              status: updatedStatus,
            };
          });

          return changed ? next : prev;
        });

        for (const quote of quotes) {
          const key = normalizeSymbol(quote.symbol);
          if (!key) continue;
          const updatedAt = String(quote.updatedAtUtc ?? '').trim();
          if (updatedAt) lastUpdatedBySymbol.set(key, updatedAt);
        }

        const nextDelayMs = Math.max(15_000, Math.min((ttlSec ?? 300) * 1000, 5 * 60 * 1000));
        scheduleNext(nextDelayMs);
      } catch (err) {
        if (disposed) return;
        if (isUnauthorized(err)) {
          onUnauthorized?.();
        } else if (!disposed) {
          scheduleNext(30000);
        }
      }
    };

    void refreshQuotes();

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      void refreshQuotes();
    };
    const onFocus = () => {
      if (document.visibilityState !== 'visible') return;
      void refreshQuotes();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);

    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      if (refreshTintTimeoutRef.current != null) {
        window.clearTimeout(refreshTintTimeoutRef.current);
        refreshTintTimeoutRef.current = null;
      }
      setIsQuotesRefreshing(false);
      if (timerId != null) window.clearTimeout(timerId);
    };
  }, [authToken, computeAlertStatus, normalizeSymbol, onUnauthorized]);

  const restoreWatchlistRow = (pending: { row: WatchlistRow; index: number }) => {
    setWatchlistRows((prev) => {
      const sym = normalizeSymbol(pending.row.symbol);
      if (prev.some((r) => normalizeSymbol(r.symbol) === sym)) return prev;
      const next = [...prev];
      const idx = Math.max(0, Math.min(pending.index, next.length));
      next.splice(idx, 0, pending.row);
      return next;
    });
  };

  const commitPendingWatchlistDeleteBySymbol = async (symbol: string) => {
    const pending = pendingWatchlistDeleteRef.current;
    if (!pending || pending.symbol !== symbol) return;

    pendingWatchlistDeleteRef.current = null;
    setPendingWatchlistDelete(null);

    if (!authToken) return;
    setWatchlistError(null);

    try {
      await adminApi.deleteMyWatchlistBySymbol(authToken, symbol);
      const items = await adminApi.listMyWatchlist(authToken);
      const rows: WatchlistRow[] = (items ?? []).map((it) => {
        const currentPrice = Number(it.currentPrice ?? 0) || 0;
        const buy = it.buyPriceAlert == null ? null : (Number(it.buyPriceAlert) || 0);
        const sell = it.sellPriceAlert == null ? null : (Number(it.sellPriceAlert) || 0);
        return {
          id: it.id,
          symbol: normalizeSymbol(it.symbol),
          currentPrice,
          changePct: Number(it.changePct ?? 0) || 0,
          buyPriceAlert: buy,
          sellPriceAlert: sell,
          status: computeAlertStatus(currentPrice, buy, sell),
        };
      });
      setWatchlistRows(rows);
    } catch (err) {
      restoreWatchlistRow({ row: pending.row, index: pending.index });
      if (isUnauthorized(err)) {
        onUnauthorized?.();
        return;
      }
      setWatchlistError(err instanceof Error ? err.message : 'Failed deleting watchlist item');
    }
  };

  const undoPendingWatchlistDelete = () => {
    const pending = pendingWatchlistDeleteRef.current;
    if (!pending) return;
    window.clearTimeout(pending.timerId);
    pendingWatchlistDeleteRef.current = null;
    setPendingWatchlistDelete(null);
    restoreWatchlistRow({ row: pending.row, index: pending.index });
  };

  const dismissPendingWatchlistAddUndo = useCallback(() => {
    const pending = pendingWatchlistAddUndoRef.current;
    if (pending) window.clearTimeout(pending.timerId);
    pendingWatchlistAddUndoRef.current = null;
    setPendingWatchlistAddUndo(null);
  }, []);

  const dismissPendingWatchlistEditUndo = useCallback(() => {
    const pending = pendingWatchlistEditUndoRef.current;
    if (pending) window.clearTimeout(pending.timerId);
    pendingWatchlistEditUndoRef.current = null;
    setPendingWatchlistEditUndo(null);
  }, []);

  const dismissAllWatchlistUndoSnackbars = useCallback(() => {
    const pendingDel = pendingWatchlistDeleteRef.current;
    if (pendingDel) {
      window.clearTimeout(pendingDel.timerId);
      void commitPendingWatchlistDeleteBySymbol(pendingDel.symbol);
    }
    dismissPendingWatchlistAddUndo();
    dismissPendingWatchlistEditUndo();
  }, [commitPendingWatchlistDeleteBySymbol, dismissPendingWatchlistAddUndo, dismissPendingWatchlistEditUndo]);

  const undoPendingWatchlistAdd = useCallback(() => {
    const pending = pendingWatchlistAddUndoRef.current;
    if (!pending) return;

    window.clearTimeout(pending.timerId);
    pendingWatchlistAddUndoRef.current = null;
    setPendingWatchlistAddUndo(null);

    if (!authToken) return;
    setWatchlistError(null);

    void (async () => {
      try {
        await adminApi.deleteMyWatchlistBySymbol(authToken, pending.symbol);
        const items = await adminApi.listMyWatchlist(authToken);
        const rows: WatchlistRow[] = (items ?? []).map((it) => {
          const currentPrice = Number(it.currentPrice ?? 0) || 0;
          const buy = it.buyPriceAlert == null ? null : (Number(it.buyPriceAlert) || 0);
          const sell = it.sellPriceAlert == null ? null : (Number(it.sellPriceAlert) || 0);
          return {
            id: it.id,
            symbol: normalizeSymbol(it.symbol),
            currentPrice,
            changePct: Number(it.changePct ?? 0) || 0,
            buyPriceAlert: buy,
            sellPriceAlert: sell,
            status: computeAlertStatus(currentPrice, buy, sell),
          };
        });

        const pendingSym = pendingWatchlistDeleteRef.current?.symbol;
        const filtered = pendingSym ? rows.filter((r) => normalizeSymbol(r.symbol) !== pendingSym) : rows;
        setWatchlistRows(filtered);
      } catch (err) {
        if (isUnauthorized(err)) {
          onUnauthorized?.();
          return;
        }
        setWatchlistError(err instanceof Error ? err.message : 'Failed undo');
      }
    })();
  }, [authToken, computeAlertStatus, normalizeSymbol, onUnauthorized]);

  const undoPendingWatchlistEdit = useCallback(() => {
    const pending = pendingWatchlistEditUndoRef.current;
    if (!pending) return;

    window.clearTimeout(pending.timerId);
    pendingWatchlistEditUndoRef.current = null;
    setPendingWatchlistEditUndo(null);

    if (!authToken) return;
    setWatchlistError(null);

    const snapshot = pending.snapshot;
    void (async () => {
      try {
        await adminApi.upsertMyWatchlistItem(authToken, {
          symbol: normalizeSymbol(snapshot.symbol),
          buyPriceAlert: snapshot.buyPriceAlert,
          sellPriceAlert: snapshot.sellPriceAlert,
          currentPrice: snapshot.currentPrice,
          changePct: snapshot.changePct,
          status: computeAlertStatus(snapshot.currentPrice, snapshot.buyPriceAlert, snapshot.sellPriceAlert),
        });

        const items = await adminApi.listMyWatchlist(authToken);
        const rows: WatchlistRow[] = (items ?? []).map((it) => {
          const currentPrice = Number(it.currentPrice ?? 0) || 0;
          const buy = it.buyPriceAlert == null ? null : (Number(it.buyPriceAlert) || 0);
          const sell = it.sellPriceAlert == null ? null : (Number(it.sellPriceAlert) || 0);
          return {
            id: it.id,
            symbol: normalizeSymbol(it.symbol),
            currentPrice,
            changePct: Number(it.changePct ?? 0) || 0,
            buyPriceAlert: buy,
            sellPriceAlert: sell,
            status: computeAlertStatus(currentPrice, buy, sell),
          };
        });

        const pendingSym = pendingWatchlistDeleteRef.current?.symbol;
        const filtered = pendingSym ? rows.filter((r) => normalizeSymbol(r.symbol) !== pendingSym) : rows;
        setWatchlistRows(filtered);
      } catch (err) {
        if (isUnauthorized(err)) {
          onUnauthorized?.();
          return;
        }
        setWatchlistError(err instanceof Error ? err.message : 'Failed undo');
      }
    })();
  }, [authToken, computeAlertStatus, normalizeSymbol, onUnauthorized]);

  // Ensure each symbol gets a persisted (random) palette color.
  useEffect(() => {
    const symbols = Array.from(new Set(watchlistRows.map((r) => normalizeSymbol(r.symbol)).filter(Boolean)));
    if (symbols.length === 0) return;
    const changed = ensureSymbolColorsAssigned(symbols);
    if (changed) setSymbolColorVersion((v) => v + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchlistRows]);

  type SortKey = keyof Pick<WatchlistRow, 'symbol' | 'currentPrice' | 'changePct' | 'buyPriceAlert' | 'sellPriceAlert' | 'status'>;
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'symbol', dir: 'asc' });

  const toggleSort = (key: SortKey) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
    });
  };

  const sortIndicator = (key: SortKey): React.ReactNode => {
    if (sort.key !== key) {
      return (
        <img
          src="/images/SortList.svg"
          alt="Sort"
          style={{ width: 16, height: 16, marginLeft: 6, verticalAlign: 'text-bottom', opacity: isDarkMode ? 0.9 : 0.75 }}
        />
      );
    }

    return sort.dir === 'asc' ? ' ↑' : ' ↓';
  };

  const formatUsd = (value: number): string => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    return `$${n.toFixed(2)}`;
  };

  const formatUsdAlert = (value: number | null): string => {
    if (value == null) return '';
    const n = Number(value);
    if (!Number.isFinite(n) || n === 0) return '';
    return `$${n.toFixed(2)}`;
  };

  const pctFormatter = useMemo(() => {
    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  }, []);

  const formatPctUpTo2 = (value: number): string => {
    const nRaw = Number(value);
    if (!Number.isFinite(nRaw)) return '—';
    const n = Math.round(nRaw * 100) / 100;
    const safe = Object.is(n, -0) ? 0 : n;
    const sign = safe > 0 ? '+' : '';
    return `${sign}${pctFormatter.format(safe)}%`;
  };

  const sortedRows = useMemo(() => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    const list = [...watchlistRows];
    list.sort((a, b) => {
      if (sort.key === 'status') {
        const av = getAlertStatusSortMeta(a);
        const bv = getAlertStatusSortMeta(b);
        if (av.rank !== bv.rank) return (av.rank - bv.rank) * dir;
        const textDiff = av.text.localeCompare(bv.text);
        if (textDiff !== 0) return textDiff * dir;
        return String(a.symbol || '').localeCompare(String(b.symbol || '')) * dir;
      }
      const av = a[sort.key];
      const bv = b[sort.key];
      // Keep blanks (null/undefined) last for both asc/desc.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
    return list;
  }, [sort.dir, sort.key, watchlistRows, getAlertStatusSortMeta]);

  const [menuOpenFor, setMenuOpenFor] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<{ top: number; left: number } | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const computeMenuAnchor = useCallback((rawSymbol: string): { top: number; left: number } | null => {
    const sym = normalizeSymbol(rawSymbol);
    const button = document.getElementById(`watchlist-actions-btn-${sym}`);
    if (!button) return null;

    const rect = button.getBoundingClientRect();
    const menuWidth = 180;
    const menuHeight = 104;
    const margin = 8;

    let left = rect.right - menuWidth;
    left = Math.max(margin, Math.min(left, window.innerWidth - menuWidth - margin));

    let top = rect.bottom + 6;
    if (top + menuHeight + margin > window.innerHeight) {
      top = Math.max(margin, rect.top - 6 - menuHeight);
    }

    return { top, left };
  }, [normalizeSymbol]);

  const toggleMenuFor = (rawSymbol: string) => {
    const sym = normalizeSymbol(rawSymbol);
    setMenuOpenFor((prev) => {
      const next = prev === sym ? null : sym;
      if (next) setMenuAnchor(computeMenuAnchor(next));
      else setMenuAnchor(null);
      return next;
    });
  };

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (!menuOpenFor) return;

      const btn = document.getElementById(`watchlist-actions-btn-${normalizeSymbol(menuOpenFor)}`);
      if (btn && btn.contains(e.target as Node)) return;

      if (menuRef.current && menuRef.current.contains(e.target as Node)) return;
      setMenuOpenFor(null);
      setMenuAnchor(null);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [menuOpenFor]);

  // Keep dropdown anchored on scroll/resize (including scrollable containers).
  useEffect(() => {
    if (!menuOpenFor) return;

    const update = () => {
      const next = computeMenuAnchor(menuOpenFor);
      if (next) setMenuAnchor(next);
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [menuOpenFor, computeMenuAnchor]);

  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [modalPrefillSymbol, setModalPrefillSymbol] = useState<string | null>(null);
  const [modalEditRow, setModalEditRow] = useState<WatchlistRow | null>(null);

  const [skipDeleteConfirm, setSkipDeleteConfirm] = useState(() => {
    try {
      return window.localStorage.getItem(WATCHLIST_SKIP_DELETE_CONFIRM_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteSymbol, setDeleteSymbol] = useState<string | null>(null);
  const [deleteDontShowAgain, setDeleteDontShowAgain] = useState(false);
  
  const openDeleteModal = (symbol: string) => {
    setDeleteSymbol(normalizeSymbol(symbol));
    setDeleteDontShowAgain(false);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeleteSymbol(null);
    setDeleteDontShowAgain(false);
  };

  const performDeleteWatchlistBySymbol = (symbol: string) => {
    const sym = normalizeSymbol(symbol);

    // Only one undo snackbar at a time.
    dismissAllWatchlistUndoSnackbars();

    if (!authToken) {
      setWatchlistError('You must be logged in to delete watchlist items.');
      return;
    }

    setWatchlistError(null);

    // If another delete is pending, commit it now (no undo).
    const existing = pendingWatchlistDeleteRef.current;
    if (existing) {
      window.clearTimeout(existing.timerId);
      void commitPendingWatchlistDeleteBySymbol(existing.symbol);
    }

    const removedIndex = watchlistRows.findIndex((r) => normalizeSymbol(r.symbol) === sym);
    const removedRow = removedIndex >= 0 ? watchlistRows[removedIndex] : null;
    if (!removedRow) return;

    setWatchlistRows((prev) => prev.filter((r) => normalizeSymbol(r.symbol) !== sym));

    const timerId = window.setTimeout(() => {
      void commitPendingWatchlistDeleteBySymbol(sym);
    }, WATCHLIST_DELETE_UNDO_MS);

    const pending: PendingWatchlistDelete = { symbol: sym, row: removedRow, index: removedIndex, timerId };
    pendingWatchlistDeleteRef.current = pending;
    setPendingWatchlistDelete(pending);
  };

  const existingSymbols = useMemo(() => {
    return new Set<string>(watchlistRows.map((r) => normalizeSymbol(r.symbol)));
  }, [watchlistRows]);

  const openAddModal = (prefillSymbol?: string | null) => {
    dismissAllWatchlistUndoSnackbars();
    setMenuOpenFor(null);
    setMenuAnchor(null);
    setModalMode('add');
    setModalEditRow(null);
    setModalPrefillSymbol(prefillSymbol ? normalizeSymbol(prefillSymbol) : null);
    setIsAddEditModalOpen(true);
  };

  const openEditModal = (row: WatchlistRow) => {
    dismissAllWatchlistUndoSnackbars();
    setMenuOpenFor(null);
    setMenuAnchor(null);
    setModalMode('edit');
    setModalPrefillSymbol(null);
    setModalEditRow(row);
    lastEditSnapshotRef.current = { ...row };
    setIsAddEditModalOpen(true);
  };

  const closeAddEditModal = () => {
    setIsAddEditModalOpen(false);
  };

  const handleModalSaved = (savedSymbol: string) => {
    if (!authToken) return;

    void (async () => {
      try {
        const items = await adminApi.listMyWatchlist(authToken);
        const rows: WatchlistRow[] = (items ?? []).map((it) => {
          const currentPrice = Number(it.currentPrice ?? 0) || 0;
          const buy = it.buyPriceAlert == null ? null : (Number(it.buyPriceAlert) || 0);
          const sell = it.sellPriceAlert == null ? null : (Number(it.sellPriceAlert) || 0);
          const storedStatus = String((it as any).status || '').trim();
          const hasAlerts = (Number.isFinite(buy) && buy !== 0) || (Number.isFinite(sell) && sell !== 0);
          return {
            id: it.id,
            symbol: normalizeSymbol(it.symbol),
            currentPrice,
            changePct: Number(it.changePct ?? 0) || 0,
            buyPriceAlert: buy,
            sellPriceAlert: sell,
            status: hasAlerts ? (storedStatus || computeAlertStatus(currentPrice, buy, sell)) : '',
          };
        });

        const pendingSym = pendingWatchlistDeleteRef.current?.symbol;
        const filtered = pendingSym ? rows.filter((r) => normalizeSymbol(r.symbol) !== pendingSym) : rows;
        setWatchlistRows(filtered);

        // Undo snackbar for add/edit actions.
        const sym = normalizeSymbol(savedSymbol);
        if (sym) {
          dismissAllWatchlistUndoSnackbars();

          if (modalMode === 'add') {
            const existing = pendingWatchlistAddUndoRef.current;
            if (existing) window.clearTimeout(existing.timerId);

            const timerId = window.setTimeout(() => {
              pendingWatchlistAddUndoRef.current = null;
              setPendingWatchlistAddUndo(null);
            }, WATCHLIST_ADD_UNDO_MS);

            pendingWatchlistAddUndoRef.current = { symbol: sym, timerId };
            setPendingWatchlistAddUndo({ message: 'Added to my Watchlist', timerId });
          }

          if (modalMode === 'edit') {
            const snapshot = lastEditSnapshotRef.current;
            if (snapshot) {
              const existing = pendingWatchlistEditUndoRef.current;
              if (existing) window.clearTimeout(existing.timerId);

              const timerId = window.setTimeout(() => {
                pendingWatchlistEditUndoRef.current = null;
                setPendingWatchlistEditUndo(null);
              }, WATCHLIST_EDIT_UNDO_MS);

              pendingWatchlistEditUndoRef.current = { snapshot, timerId };
              setPendingWatchlistEditUndo({ message: 'Watchlist updated', timerId });
            }

            lastEditSnapshotRef.current = null;
          }
        }
      } catch (err) {
        if (isUnauthorized(err)) {
          onUnauthorized?.();
          return;
        }
        setWatchlistError(err instanceof Error ? err.message : 'Failed loading watchlist');
      }
    })();
  };

  const pageStyle: React.CSSProperties = {
    // Align with Header left padding (see Header.tsx headerStyle padding-left: 3.2rem)
    padding: '9px 10px 18px 3.2rem',
    backgroundColor: colors.pageBg,
    minHeight: 'calc(100vh - 4.375rem)',
    height: 'calc(100vh - 4.375rem)',
    color: colors.text,
    overflow: 'hidden',
  };

  const contentStyle: React.CSSProperties = {
    width: 1280,
    maxWidth: '100%',
    display: 'grid',
    gap: 0,
    height: '100%',
  };

  const stickyAppHeaderHeight = 56;
  const stickyTopBarHeight = 44;
  const stickyTopBarOffset = 0;
  const tableHeaderHeight = 56;
  const tableHeaderBackground = colors.headerBg;
  const tableScrollbarPadding = Math.max(0, tableScrollbarWidth + 1);
  const tableBodyMaxHeight = `calc(100vh - ${stickyTopBarOffset + stickyTopBarHeight + 117 + tableHeaderHeight}px)`;

  const topBarStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    position: 'sticky',
    top: stickyTopBarOffset,
    zIndex: 7,
    backgroundColor: colors.pageBg,
    padding: '4px 0 2px',
    minHeight: stickyTopBarHeight,
    boxSizing: 'border-box',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: 22,
    fontWeight: 800,
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 5,
  };

  const addButtonStyle: React.CSSProperties = {
    width: 150,
    height: 37,
    padding: '4px 14px',
    borderRadius: 6,
    border: `1px solid ${colors.buttonBorder}`,
    backgroundColor: colors.buttonBg,
    color: colors.buttonText,
    fontWeight: 700,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    whiteSpace: 'nowrap',
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: colors.cardBg,
    border: `1px solid ${colors.border}`,
    borderRadius: 10,
    overflow: 'hidden',
  };

  const tableWrapStyle: React.CSSProperties = {
    overflowX: 'auto',
    position: 'relative',
    maxHeight: tableBodyMaxHeight,
    height: tableBodyMaxHeight,
    overflowY: 'auto',
    scrollbarGutter: hasTableScrollbar ? 'stable' : 'auto',
    boxSizing: 'border-box',
    paddingBottom: 8,
    outline: 'none',
    boxShadow: 'none',
  };

  const tableHeaderWrapStyle: React.CSSProperties = {
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingRight: hasTableScrollbar ? tableScrollbarPadding : 0,
    boxSizing: 'content-box',
    backgroundColor: tableHeaderBackground,
  };

  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    tableLayout: 'fixed',
  };

  const thBase: React.CSSProperties = {
    position: 'sticky',
    top: 0,
    zIndex: 6,
    textAlign: 'center',
    fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
    fontWeight: 500,
    fontSize: 14,
    lineHeight: '100%',
    letterSpacing: '1%',
    textTransform: 'none',
    width: 160,
    height: 56,
    padding: '6px 8px',
    background: colors.headerBg,
    color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
    borderBottom: `1.13px solid ${colors.border}`,
    borderLeft: `1.13px solid ${colors.border}`,
    borderTop: '0px solid transparent',
    borderRight: '0px solid transparent',
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
  };

  const tdBase: React.CSSProperties = {
    padding: '10px 10px',
    borderBottom: `1px solid ${colors.border}`,
    borderRight: `1px solid ${colors.border}`,
    fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
    fontWeight: 600,
    fontSize: 14,
    lineHeight: '100%',
    letterSpacing: '0.14px',
    color: colors.text,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    backgroundClip: 'padding-box',
  };

  const thClickable = (key: SortKey): React.CSSProperties => ({
    ...thBase,
    cursor: 'pointer',
    userSelect: 'none',
  });

  const colWidths = {
    symbol: 160,
    currentPrice: 150,
    changePct: 140,
    buyPriceAlert: 160,
    sellPriceAlert: 160,
    status: 176,
    actions: 130,
  };

  const barColorForSymbol = (symbol: string): string => {
    // use version to re-render once newly-assigned colors are persisted
    void symbolColorVersion;
    return getSymbolColor(symbol);
  };

  const ChangeArrowIcon: React.FC<{ dir: 'up' | 'down'; color: string }> = ({ dir, color }) => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      aria-hidden="true"
      focusable="false"
      style={{ display: 'inline', marginLeft: 4 }}
    >
      {dir === 'up' ? (
        <path d="M6 2 L10 8 L2 8 Z" fill={color} />
      ) : (
        <path d="M6 10 L2 4 L10 4 Z" fill={color} />
      )}
    </svg>
  );

  const actionsButtonStyle: React.CSSProperties = {
    border: 'none',
    background: 'transparent',
    color: colors.text,
    padding: '6px 8px',
    borderRadius: 10,
    fontWeight: 400,
    cursor: 'pointer',
    outline: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    width: 98,
  };

  const menuStyle: React.CSSProperties = {
    position: 'absolute',
    right: 0,
    top: 'calc(100% + 6px)',
    zIndex: 5000,
    minWidth: 180,
    backgroundColor: colors.menuBg,
    border: `1px solid ${colors.border}`,
    borderRadius: 10,
    boxShadow: colors.menuShadow,
    overflow: 'hidden',
  };

  const menuItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    padding: '10px 12px',
    border: 0,
    backgroundColor: 'transparent',
    color: colors.text,
    cursor: 'pointer',
    textAlign: 'left',
    fontSize: 13,
    fontWeight: 600,
    fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
  };

  const menuIconStyle: React.CSSProperties = { width: 16, height: 16, display: 'block', flex: '0 0 auto' };

  const modalOverlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '2rem',
  };

  const deleteModalStyle: React.CSSProperties = {
    width: 432,
    height: 210,
    backgroundColor: '#FFFFFF',
    borderRadius: 0,
    boxShadow: colors.menuShadow,
    border: `1px solid ${colors.border}`,
    padding: 20,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    opacity: 1,
  };

  const modalTextStyle: React.CSSProperties = {
    fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
    fontWeight: 400,
    fontSize: 16,
    lineHeight: '100%',
    letterSpacing: 0,
    color: '#0D0D0D',
  };

  const addTitleTextStyle: React.CSSProperties = {
    fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
    fontWeight: 600,
    fontStyle: 'normal',
    fontSize: 20,
    lineHeight: '100%',
    letterSpacing: 0,
    color: '#000000',
  };

  const symbolLinkStyle: React.CSSProperties = {
    border: 0,
    backgroundColor: 'transparent',
    padding: 0,
    margin: 0,
    font: 'inherit',
    color: colors.text,
    cursor: onOpenResearch ? 'pointer' : 'default',
    fontWeight: 800,
    textAlign: 'left',
  };

  return (
    <div style={pageStyle}>
      <div style={contentStyle}>
        <div id="watchlist-title-row" style={topBarStyle}>
          <div>
            <h1 style={titleStyle}>My Watchlist</h1>
          </div>

          <button
            type="button"
            style={addButtonStyle}
            onClick={() => {
              openAddModal();
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, transform: 'translateX(20px)' }}>
              <span style={{ fontSize: 16, lineHeight: 1 }}>+</span>
              <span>Add Stock</span>
            </span>
          </button>
        </div>

        {watchlistError && (
          <div
            style={{
              margin: '0 0 12px 0',
              maxWidth: 1600,
              padding: 12,
              borderRadius: 10,
              border: `1px solid ${colors.border}`,
              background: isDarkMode ? 'rgba(255,255,255,0.03)' : '#fff7ed',
              color: isDarkMode ? colors.text : '#9a3412',
              fontWeight: 800,
              fontSize: 13,
            }}
          >
            {watchlistError}
          </div>
        )}
        <div style={cardStyle}>
          <div style={tableHeaderWrapStyle}>
            <table style={tableStyle} aria-label="My Watchlist">
              <colgroup>
                <col style={{ width: colWidths.symbol }} />
                <col style={{ width: colWidths.currentPrice }} />
                <col style={{ width: colWidths.changePct }} />
                <col style={{ width: colWidths.buyPriceAlert }} />
                <col style={{ width: colWidths.sellPriceAlert }} />
                <col style={{ width: colWidths.status }} />
                <col style={{ width: colWidths.actions }} />
              </colgroup>
              <thead>
                <tr>
                  <th
                    style={{ ...thClickable('symbol'), borderTopLeftRadius: 10 }}
                    onClick={() => toggleSort('symbol')}
                  >
                    Stock Symbol{sortIndicator('symbol')}
                  </th>
                  <th style={thClickable('currentPrice')} onClick={() => toggleSort('currentPrice')}>
                    Current Price{sortIndicator('currentPrice')}
                  </th>
                  <th style={thClickable('changePct')} onClick={() => toggleSort('changePct')}>
                    Change (%){sortIndicator('changePct')}
                  </th>
                  <th style={thClickable('buyPriceAlert')} onClick={() => toggleSort('buyPriceAlert')}>
                    Buy Price Alert{sortIndicator('buyPriceAlert')}
                  </th>
                  <th style={thClickable('sellPriceAlert')} onClick={() => toggleSort('sellPriceAlert')}>
                    Sell Price Alert{sortIndicator('sellPriceAlert')}
                  </th>
                  <th style={thClickable('status')} onClick={() => toggleSort('status')}>
                    Alert Status{sortIndicator('status')}
                  </th>
                  <th style={{ ...thBase, borderRight: 'none', borderTopRightRadius: 10 }}>Actions</th>
                </tr>
              </thead>
            </table>
          </div>
          <div
            ref={tableWrapRef}
            style={tableWrapStyle}
            tabIndex={0}
            role="region"
            aria-label="Watchlist rows"
            onKeyDown={handleTableKeyDown}
          >
            <table style={tableStyle} aria-label="My Watchlist rows">
              <colgroup>
                <col style={{ width: colWidths.symbol }} />
                <col style={{ width: colWidths.currentPrice }} />
                <col style={{ width: colWidths.changePct }} />
                <col style={{ width: colWidths.buyPriceAlert }} />
                <col style={{ width: colWidths.sellPriceAlert }} />
                <col style={{ width: colWidths.status }} />
                <col style={{ width: colWidths.actions }} />
              </colgroup>
              <tbody>
                {sortedRows.map((r) => {
                  const sym = normalizeSymbol(r.symbol);
                  const actionsBtnId = `watchlist-actions-btn-${sym}`;
                  const actionsMenuId = `watchlist-actions-menu-${sym}`;
                  return (
                    <tr
                      key={r.symbol}
                      style={{
                        backgroundColor: 'transparent',
                        cursor: 'default',
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.backgroundColor = colors.hoverBg;
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'transparent';
                      }}
                    >
                      <td style={{ ...tdBase, textAlign: 'left' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span
                            aria-hidden="true"
                            style={{
                              width: 5,
                              height: 22,
                              borderRadius: 999,
                              backgroundColor: barColorForSymbol(r.symbol),
                              display: 'inline-block',
                            }}
                          />
                          <button
                            type="button"
                            style={symbolLinkStyle}
                            onClick={() => {
                              if (!onOpenResearch) return;
                              onOpenResearch(normalizeSymbol(r.symbol));
                            }}
                            aria-label={`Open research for ${r.symbol}`}
                            disabled={!onOpenResearch}
                          >
                            {r.symbol}
                          </button>
                        </div>
                      </td>
                      <td
                        style={{
                          ...tdBase,
                          color: isQuotesRefreshing ? '#8a8a8a' : tdBase.color,
                        }}
                      >
                        {formatUsd(r.currentPrice)}
                      </td>
                      <td style={tdBase}>
                        {(() => {
                          const c = Number(r.changePct);
                          const isPos = Number.isFinite(c) && c > 0;
                          const isNeg = Number.isFinite(c) && c < 0;
                          const color = isPos ? colors.success : isNeg ? colors.buy : colors.subText;
                          return (
                            <span style={{ display: 'inline-flex', alignItems: 'center', fontWeight: 900, color }}>
                              {formatPctUpTo2(c)}
                              {isPos && <ChangeArrowIcon dir="up" color={color} />}
                              {isNeg && <ChangeArrowIcon dir="down" color={color} />}
                            </span>
                          );
                        })()}
                      </td>
                      <td style={tdBase}>{formatUsdAlert(r.buyPriceAlert)}</td>
                      <td style={tdBase}>{formatUsdAlert(r.sellPriceAlert)}</td>
                      <td style={{ ...tdBase, textAlign: 'center', overflow: 'visible', textOverflow: 'clip' }}>{renderAlertStatus(r)}</td>
                      <td style={{ ...tdBase, borderRight: 'none' }}>
                        <button
                          type="button"
                          style={actionsButtonStyle}
                          onClick={() => toggleMenuFor(sym)}
                          aria-haspopup="menu"
                          aria-expanded={menuOpenFor === sym}
                          aria-controls={actionsMenuId}
                          id={actionsBtnId}
                        >
                          <span>Actions</span>
                          <img
                            src="/images/action-down.svg"
                            alt=""
                            aria-hidden="true"
                            style={{ width: 14, height: 14, display: 'block', opacity: isDarkMode ? 0.9 : 0.8 }}
                          />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {menuOpenFor && menuAnchor &&
          (() => {
            const sym = normalizeSymbol(menuOpenFor);
            const row = watchlistRows.find((r) => normalizeSymbol(r.symbol) === sym);
            if (!row) return null;

            const actionsBtnId = `watchlist-actions-btn-${sym}`;
            const actionsMenuId = `watchlist-actions-menu-${sym}`;
            const portalMenuStyle: React.CSSProperties = {
              ...menuStyle,
              position: 'fixed',
              top: menuAnchor.top,
              left: menuAnchor.left,
              right: 'auto',
            };

            return createPortal(
              <div
                ref={menuRef}
                id={actionsMenuId}
                style={portalMenuStyle}
                role="menu"
                aria-label={`${sym} actions`}
                aria-labelledby={actionsBtnId}
              >
                <button
                  type="button"
                  style={{ ...menuItemStyle, borderBottom: `1px solid ${colors.border}` }}
                  onClick={() => {
                    setMenuOpenFor(null);
                    setMenuAnchor(null);
                    openEditModal(row);
                  }}
                  role="menuitem"
                  onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.hoverBg)}
                  onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent')}
                >
                  <img src="/images/action-edit.svg" alt="" aria-hidden="true" style={menuIconStyle} />
                  <span>Edit</span>
                </button>
                <button
                  type="button"
                  style={menuItemStyle}
                  onClick={() => {
                    setMenuOpenFor(null);
                    setMenuAnchor(null);
                    if (!authToken) {
                      setWatchlistError('You must be logged in to delete watchlist items.');
                      return;
                    }

                    if (skipDeleteConfirm) {
                      performDeleteWatchlistBySymbol(sym);
                      return;
                    }

                    openDeleteModal(sym);
                  }}
                  role="menuitem"
                  onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.hoverBg)}
                  onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent')}
                >
                  <img src="/images/action-delete.svg" alt="" aria-hidden="true" style={menuIconStyle} />
                  <span>Delete</span>
                </button>
              </div>,
              document.body
            );
          })()}

        <WatchlistAddEditModal
          isDarkMode={isDarkMode}
          open={isAddEditModalOpen}
          mode={modalMode}
          prefillSymbol={modalMode === 'add' ? modalPrefillSymbol : null}
          editRow={
            modalMode === 'edit' && modalEditRow
              ? {
                  symbol: modalEditRow.symbol,
                  currentPrice: modalEditRow.currentPrice,
                  changePct: modalEditRow.changePct,
                  buyPriceAlert: modalEditRow.buyPriceAlert,
                  sellPriceAlert: modalEditRow.sellPriceAlert,
                }
              : null
          }
          existingSymbols={modalMode === 'add' ? existingSymbols : undefined}
          authToken={authToken}
          onUnauthorized={onUnauthorized}
          onClose={closeAddEditModal}
          onSaved={handleModalSaved}
        />

        {isDeleteModalOpen && deleteSymbol && (
          <div
            style={modalOverlayStyle}
            onClick={closeDeleteModal}
            id="watchlist-delete-modal-overlay"
            data-testid="watchlist-delete-modal-overlay"
          >
            <div
              style={deleteModalStyle}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Delete this stock"
              id="watchlist-delete-modal"
              data-testid="watchlist-delete-modal"
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <img src="/images/action-delete.svg" alt="" aria-hidden="true" style={{ width: 16, height: 16, display: 'block', flex: '0 0 auto' }} />
                  <div style={{ ...addTitleTextStyle, whiteSpace: 'nowrap' }}>Delete this stock?</div>
                </div>

                <button
                  type="button"
                  onClick={closeDeleteModal}
                  aria-label="Close"
                  title="Close"
                  id="watchlist-delete-modal-close"
                  data-testid="watchlist-delete-modal-close"
                  style={{
                    border: 'none',
                    background: 'transparent',
                    padding: 0,
                    cursor: 'pointer',
                    color: '#0D0D0D',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img src="/images/remove.svg" alt="Close" style={{ width: 24, height: 24, display: 'block' }} />
                </button>
              </div>

              <div
                style={{
                  fontFamily: modalTextStyle.fontFamily,
                  fontWeight: 400,
                  fontSize: 16,
                  lineHeight: '20px',
                  letterSpacing: 0,
                  verticalAlign: 'middle',
                  color: '#5A5A5A',
                }}
              >
                You're about to delete this stock from your watchlist.
              </div>

              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 10,
                  fontFamily: modalTextStyle.fontFamily,
                  fontWeight: 400,
                  fontSize: 16,
                  lineHeight: '20px',
                  letterSpacing: 0,
                  verticalAlign: 'middle',
                  color: '#0D0D0D',
                }}
                id="watchlist-delete-dont-show-again-label"
                data-testid="watchlist-delete-dont-show-again-label"
              >
                <input
                  type="checkbox"
                  checked={deleteDontShowAgain}
                  onChange={(e) => setDeleteDontShowAgain(e.target.checked)}
                  id="watchlist-delete-dont-show-again"
                  data-testid="watchlist-delete-dont-show-again"
                  style={{
                    width: 16,
                    height: 16,
                    margin: 0,
                    WebkitAppearance: 'none',
                    appearance: 'none',
                    borderRadius: 4,
                    border: `1px solid ${deleteDontShowAgain ? '#2F58F2' : '#D9D9D9'}`,
                    backgroundColor: deleteDontShowAgain ? '#2F58F2' : 'transparent',
                    backgroundImage: deleteDontShowAgain
                      ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 12'%3E%3Cpath d='M2 6.5l3.2 3.2L14 1.9' stroke='%23fff' stroke-width='2.2' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`
                      : 'none',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    cursor: 'pointer',
                    flex: '0 0 auto',
                  }}
                />
                <span>Don't show again</span>
              </label>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 18, marginTop: 'auto' }}>
                <button
                  type="button"
                  id="watchlist-delete-modal-confirm"
                  data-testid="watchlist-delete-modal-confirm"
                  onClick={() => {
                    if (deleteDontShowAgain) {
                      try {
                        window.localStorage.setItem(WATCHLIST_SKIP_DELETE_CONFIRM_KEY, '1');
                        setSkipDeleteConfirm(true);
                      } catch {
                        // ignore
                      }
                    }

                    performDeleteWatchlistBySymbol(deleteSymbol);
                    closeDeleteModal();
                  }}
                  style={{
                    background: '#EF4444',
                    border: 'none',
                    color: '#FFFFFF',
                    width: 170,
                    height: 40,
                    borderRadius: 10,
                    cursor: 'pointer',
                    fontWeight: 700,
                    fontSize: 16,
                    fontFamily: modalTextStyle.fontFamily,
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        <div
          style={{
            marginTop: 12,
            textAlign: 'left',
            fontSize: 12,
            color: colors.subText,
            fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
          }}
        >
          Data powered by{' '}
          <a
            href="https://fiscal.ai"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: colors.buttonText,
              fontWeight: 700,
              textDecoration: 'underline',
            }}
          >
            Fiscal.ai
          </a>
        </div>

        <UndoSnackbar
          open={!!pendingWatchlistDelete}
          message="Stock has been removed"
          onUndo={undoPendingWatchlistDelete}
          onDismiss={() => {
            const pending = pendingWatchlistDeleteRef.current;
            if (!pending) return;
            window.clearTimeout(pending.timerId);
            void commitPendingWatchlistDeleteBySymbol(pending.symbol);
          }}
        />

        <UndoSnackbar
          open={!!pendingWatchlistAddUndo}
          message={pendingWatchlistAddUndo?.message ?? ''}
          onUndo={undoPendingWatchlistAdd}
          onDismiss={dismissPendingWatchlistAddUndo}
        />

        <UndoSnackbar
          open={!!pendingWatchlistEditUndo}
          message={pendingWatchlistEditUndo?.message ?? ''}
          onUndo={undoPendingWatchlistEdit}
          onDismiss={dismissPendingWatchlistEditUndo}
        />
      </div>
    </div>
  );
};

export default WatchlistScreen;
